// "Acompanhe o progresso" timeline for the proposal details drawer, including the
// rental accepted flow that reuses the RentProgress fallback status payload and
// the purchase (CCV/TIC) flow derived from the offer payload itself.
//
// Every CTA returned here maps to an operation that exists in the backend:
//   - generate_contract  -> POST /property_offers/:id/contract/generate
//   - contract           -> GET  /property_offers/:id/contract (drawer)
//   - kyc / capture_kyc / submit_kyc -> KYC + FE-AUD-89 diligence gate surfaces
//   - reservation        -> reservation hold + payment ledger panel
//   - transaction_checklist -> /property_transactions (B13 closing checklist)
//   - chat_client / owner_contact -> conversation + owner WhatsApp shortcuts
// When an operation is not available the action is still returned, but disabled
// and carrying a concrete `blockReason` derived from the payload, so the UI never
// shows a dead CTA and never hides the reason why the step is stuck.
import { buildFallbackOfferStatusPayload } from 'dashboard/helper/lais/offerPresenter';
import {
  contractForOffer,
  contractSignatureDescription,
  isContractSent,
  isContractSigned,
  isKycApproved,
  isRentalOffer,
  kycForOffer,
  offerAcceptedSummary,
  ownerContactFromOffer,
} from './offerReadModel';

// Tracking identifiers follow the existing `<domain>_step_action_clicked`
// convention already emitted by the page for the rental track.
export const PROGRESS_STEP_ACTION_EVENT = 'rent_progress_step_action_clicked';
export const PURCHASE_PROGRESS_STEP_ACTION_EVENT =
  'purchase_progress_step_action_clicked';

export const PURCHASE_PROGRESS_STEP_KEYS = Object.freeze({
  RECEIVED: 'purchaseOfferReceived',
  ACCEPTED: 'purchaseOfferAccepted',
  TIC: 'purchaseTicIssuance',
  SIGNATURE: 'purchaseDocumentSignature',
  CLOSING: 'purchaseSaleClosing',
});

export const PURCHASE_PROGRESS_ACTION_KEYS = Object.freeze({
  CHAT_CLIENT: 'chat_client',
  OWNER_CONTACT: 'owner_contact',
  RESERVATION: 'reservation',
  KYC: 'kyc',
  GENERATE_CONTRACT: 'generate_contract',
  CONTRACT: 'contract',
  TRANSACTION_CHECKLIST: 'transaction_checklist',
});

const TRANSACTION_DONE_STATUSES = ['completed', 'concluded', 'finished'];
const TRANSACTION_CANCELED_STATUSES = ['canceled', 'cancelled'];

const asArray = value => (Array.isArray(value) ? value : []);

const normalizedStatus = value => String(value ?? '').toLowerCase();

const numberOrZero = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const timelineStepStateForUi = step => {
  if (step?.state === 'concluido') return 'done';
  if (step?.state === 'cancelado') return 'error';
  if (step?.current) return 'current';
  return 'pending';
};

export const kycStepState = (offer, approved) => {
  if (offer.status !== 'accepted') return 'pending';
  return approved ? 'done' : 'current';
};

export const contractPreparationStepState = (sent, kycApproved) => {
  if (sent) return 'done';
  return kycApproved ? 'current' : 'pending';
};

export const contractSignatureStepState = (signed, sent) => {
  if (signed) return 'done';
  return sent ? 'current' : 'pending';
};

export const canGenerateContractForOffer = offer => {
  const contract = contractForOffer(offer);
  return (
    offer?.status === 'accepted' &&
    !['sent', 'partially_signed', 'signed'].includes(contract?.status)
  );
};

export const progressStepAction = ({
  key,
  label,
  icon,
  variant = 'ghost',
  event = PROGRESS_STEP_ACTION_EVENT,
  disabled = false,
  hint = '',
  blockReason = '',
}) => ({
  key,
  label,
  icon,
  variant,
  event,
  disabled,
  // A blocked action always carries its reason in `hint` too, so surfaces that
  // only render one field still explain why the CTA is unavailable.
  hint: hint || blockReason,
  blockReason,
});

const purchaseStepAction = options =>
  progressStepAction({
    event: PURCHASE_PROGRESS_STEP_ACTION_EVENT,
    ...options,
  });

/* -------------------------------------------------------------------------- */
/* Purchase payload readers                                                    */
/* -------------------------------------------------------------------------- */

// FE-AUD-89 gate, only present when the offer was loaded with
// `include_kyc_diligence` (offer#show). Absent on list payloads.
export const offerDiligenceGate = offer =>
  offer?.kyc_diligence || offer?.kycDiligence || null;

export const offerDiligenceBlockers = offer =>
  asArray(offerDiligenceGate(offer)?.blockers);

export const isDiligenceBlocked = offer => {
  const gate = offerDiligenceGate(offer);
  if (!gate) return false;
  if (gate.can_advance === false || gate.canAdvance === false) return true;
  return normalizedStatus(gate.status) === 'blocked';
};

export const diligenceBlockReason = offer => {
  if (!isDiligenceBlocked(offer)) return '';

  const blocker = offerDiligenceBlockers(offer)[0];
  if (!blocker) {
    return 'Checklist de KYC, crédito e diligência com pendências obrigatórias.';
  }

  return (
    [blocker.label, blocker.reason || blocker.next_action]
      .filter(Boolean)
      .join(': ') ||
    'Checklist de KYC, crédito e diligência com pendências obrigatórias.'
  );
};

export const reservationHoldForOffer = offer =>
  offer?.reservation_hold || offer?.reservationHold || null;

export const reservationPaymentSummaryForOffer = offer =>
  offer?.reservation_payment_summary || offer?.reservationPaymentSummary || {};

// The closing transaction (B13) is fetched by reference
// (`/property_transactions?offer_id=`) and merged into the offer by the page.
export const closingTransactionForOffer = offer =>
  offer?.property_transaction ||
  offer?.propertyTransaction ||
  offer?.closing_transaction ||
  offer?.transaction ||
  null;

// Mirrors the backend gate: when FE-AUD-89 is loaded it is the source of truth,
// otherwise fall back to the legacy KYC verification attached to the offer.
export const purchaseDocumentationPending = offer => {
  if (offerDiligenceGate(offer)) return isDiligenceBlocked(offer);
  return !isKycApproved(kycForOffer(offer));
};

export const purchaseDocumentationReason = offer => {
  const gateReason = diligenceBlockReason(offer);
  if (gateReason) return gateReason;
  if (!isKycApproved(kycForOffer(offer))) {
    return 'O KYC do interessado ainda não foi aprovado; conclua a validação para liberar o TIC/CCV.';
  }
  return '';
};

// Why the TIC/CCV cannot be issued right now. Empty string == it can.
export const purchaseContractBlockReason = offer => {
  if (offer?.status !== 'accepted') {
    return 'A proposta precisa ser aceita antes de emitir o TIC/CCV.';
  }

  const contract = contractForOffer(offer);
  if (isContractSigned(contract)) {
    return 'O CCV já está assinado; não há novo documento a emitir.';
  }
  if (isContractSent(contract)) {
    return 'O CCV já foi enviado para assinatura; cancele o documento atual antes de emitir outro.';
  }

  return purchaseDocumentationReason(offer);
};

export const purchaseClosingBlockReason = offer => {
  if (closingTransactionForOffer(offer)) return '';
  if (!isContractSigned(contractForOffer(offer))) {
    return 'A transação de fechamento é aberta depois da assinatura do CCV.';
  }
  return 'A transação de fechamento ainda não foi aberta para esta proposta.';
};

/* -------------------------------------------------------------------------- */
/* Purchase step actions                                                       */
/* -------------------------------------------------------------------------- */

const reservationHint = offer => {
  const summary = reservationPaymentSummaryForOffer(offer);
  if (summary.divergent === true) {
    return 'Pagamento da reserva divergente; concilie o extrato antes de seguir.';
  }
  const outstanding = numberOrZero(
    summary.outstanding_amount ?? summary.outstandingAmount
  );
  if (outstanding > 0) {
    return 'Reserva com valor em aberto; confira o extrato de pagamento.';
  }
  return '';
};

const closingChecklistHint = transaction => {
  if (
    transaction?.mandatory_items_pending === true ||
    transaction?.mandatoryItemsPending === true
  ) {
    return 'Há itens obrigatórios pendentes no checklist da transação.';
  }
  const pendingItems = numberOrZero(
    transaction?.item_counts?.pending ?? transaction?.itemCounts?.pending
  );
  return pendingItems > 0
    ? 'Itens do checklist da transação ainda em aberto.'
    : '';
};

const purchaseAcceptedStepActions = offer => {
  const actions = [
    purchaseStepAction({
      key: PURCHASE_PROGRESS_ACTION_KEYS.CHAT_CLIENT,
      label: 'Conversar com cliente',
      icon: 'i-lucide-message-circle',
    }),
  ];

  const owner = ownerContactFromOffer(offer);
  if (
    owner?.phone_number ||
    owner?.phoneNumber ||
    offer?.metadata?.owner_phone
  ) {
    actions.push(
      purchaseStepAction({
        key: PURCHASE_PROGRESS_ACTION_KEYS.OWNER_CONTACT,
        label: 'Falar com proprietário',
        icon: 'i-lucide-phone',
      })
    );
  }

  const hold = reservationHoldForOffer(offer);
  if (!hold) {
    actions.push(
      purchaseStepAction({
        key: PURCHASE_PROGRESS_ACTION_KEYS.RESERVATION,
        label: 'Abrir reserva',
        icon: 'i-lucide-hand-coins',
        disabled: true,
        blockReason:
          'Nenhuma reserva ou sinal foi registrado para esta proposta.',
      })
    );
    return actions;
  }

  actions.push(
    purchaseStepAction({
      key: PURCHASE_PROGRESS_ACTION_KEYS.RESERVATION,
      label: 'Abrir reserva',
      icon: 'i-lucide-hand-coins',
      hint: reservationHint(offer),
    })
  );

  return actions;
};

const purchaseTicStepActions = offer => {
  const blockReason = purchaseContractBlockReason(offer);
  const contract = contractForOffer(offer);
  const actions = [];

  if (!blockReason) {
    actions.push(
      purchaseStepAction({
        key: PURCHASE_PROGRESS_ACTION_KEYS.GENERATE_CONTRACT,
        label: 'Emitir TIC e CCV',
        icon: 'i-lucide-file-signature',
        variant: 'primary',
      })
    );
  } else if (isContractSent(contract) || isContractSigned(contract)) {
    // The document already exists: the real operation here is opening it.
    actions.push(
      purchaseStepAction({
        key: PURCHASE_PROGRESS_ACTION_KEYS.CONTRACT,
        label: 'Abrir contrato',
        icon: 'i-lucide-file-check-2',
        variant: 'primary',
        hint: blockReason,
      })
    );
  } else {
    actions.push(
      purchaseStepAction({
        key: PURCHASE_PROGRESS_ACTION_KEYS.GENERATE_CONTRACT,
        label: 'Emitir TIC e CCV',
        icon: 'i-lucide-file-signature',
        variant: 'primary',
        disabled: true,
        blockReason,
      })
    );
  }

  if (purchaseDocumentationPending(offer)) {
    actions.push(
      purchaseStepAction({
        key: PURCHASE_PROGRESS_ACTION_KEYS.KYC,
        label: 'Abrir KYC e diligência',
        icon: 'i-lucide-shield-check',
        hint:
          purchaseDocumentationReason(offer) ||
          'Documentação do interessado pendente de aprovação.',
      })
    );
  }

  return actions;
};

const purchaseSignatureStepActions = offer => {
  const contract = contractForOffer(offer);
  if (!contract) {
    return [
      purchaseStepAction({
        key: PURCHASE_PROGRESS_ACTION_KEYS.CONTRACT,
        label: 'Abrir contrato',
        icon: 'i-lucide-file-check-2',
        disabled: true,
        blockReason:
          'O TIC/CCV ainda não foi emitido; não há documento para assinar.',
      }),
    ];
  }

  return [
    purchaseStepAction({
      key: PURCHASE_PROGRESS_ACTION_KEYS.CONTRACT,
      label: isContractSigned(contract)
        ? 'Ver contrato assinado'
        : 'Acompanhar assinaturas',
      icon: 'i-lucide-file-check-2',
      variant: 'primary',
      hint: contractSignatureDescription(offer),
    }),
  ];
};

const purchaseClosingStepActions = offer => {
  const blockReason = purchaseClosingBlockReason(offer);
  if (blockReason) {
    return [
      purchaseStepAction({
        key: PURCHASE_PROGRESS_ACTION_KEYS.TRANSACTION_CHECKLIST,
        label: 'Abrir checklist de fechamento',
        icon: 'i-lucide-list-checks',
        disabled: true,
        blockReason,
      }),
    ];
  }

  return [
    purchaseStepAction({
      key: PURCHASE_PROGRESS_ACTION_KEYS.TRANSACTION_CHECKLIST,
      label: 'Abrir checklist de fechamento',
      icon: 'i-lucide-list-checks',
      variant: 'primary',
      hint: closingChecklistHint(closingTransactionForOffer(offer)),
    }),
  ];
};

export const purchaseProgressActionsForStep = (offer, key) => {
  if (offer?.status !== 'accepted') return [];

  if (key === PURCHASE_PROGRESS_STEP_KEYS.ACCEPTED) {
    return purchaseAcceptedStepActions(offer);
  }
  if (key === PURCHASE_PROGRESS_STEP_KEYS.TIC) {
    return purchaseTicStepActions(offer);
  }
  if (key === PURCHASE_PROGRESS_STEP_KEYS.SIGNATURE) {
    return purchaseSignatureStepActions(offer);
  }
  if (key === PURCHASE_PROGRESS_STEP_KEYS.CLOSING) {
    return purchaseClosingStepActions(offer);
  }

  return [];
};

export const progressActionsForStep = (offer, step) => {
  const key = step?.key;
  if (offer?.status !== 'accepted') return [];
  if (!isRentalOffer(offer)) return purchaseProgressActionsForStep(offer, key);

  if (
    ['docsDemandPending', 'docsDemandResend', 'tenantRejected'].includes(key)
  ) {
    const resend = ['docsDemandResend', 'tenantRejected'].includes(key);
    return [
      progressStepAction({
        key: 'capture_kyc',
        label: 'Capturar KYC',
        icon: 'i-lucide-scan-face',
      }),
      progressStepAction({
        key: 'submit_kyc',
        label: resend ? 'Reenviar KYC' : 'Enviar KYC',
        icon: 'i-lucide-shield-check',
        variant: 'primary',
      }),
      progressStepAction({
        key: 'chat_client',
        label: 'Conversar com cliente',
        icon: 'i-lucide-message-circle',
      }),
    ];
  }

  if (
    [
      'docsQuintoandarAnalysis',
      'tenantWithMultipleDocs',
      'waitingForTenantToDecide',
    ].includes(key)
  ) {
    return [
      progressStepAction({
        key: 'chat_client',
        label: 'Conversar com cliente',
        icon: 'i-lucide-message-circle',
      }),
    ];
  }

  if (key === 'docsSupplyPending') {
    return [
      progressStepAction({
        key: 'owner_contact',
        label: 'Falar com proprietário',
        icon: 'i-lucide-phone',
      }),
    ];
  }

  if (key === 'docsApproved' && canGenerateContractForOffer(offer)) {
    return [
      progressStepAction({
        key: 'generate_contract',
        label: 'Gerar contrato',
        icon: 'i-lucide-file-signature',
        variant: 'primary',
      }),
    ];
  }

  if (
    key === 'contractQuintoandarInCreation' &&
    canGenerateContractForOffer(offer)
  ) {
    return [
      progressStepAction({
        key: 'generate_contract',
        label: 'Gerar contrato',
        icon: 'i-lucide-file-signature',
        variant: 'primary',
      }),
    ];
  }

  return [];
};

export const rentalAcceptedOfferSteps = offer =>
  buildFallbackOfferStatusPayload(offer).steps.map(step => ({
    key: step.key,
    label: step.label,
    description: step.description,
    state: timelineStepStateForUi(step),
    actions: progressActionsForStep(offer, step),
  }));

/* -------------------------------------------------------------------------- */
/* Purchase track                                                              */
/* -------------------------------------------------------------------------- */

export const purchaseTicStepState = offer => {
  const contract = contractForOffer(offer);
  if (isContractSent(contract) || isContractSigned(contract)) return 'done';
  if (offer?.status !== 'accepted') return 'pending';
  return 'current';
};

export const purchaseClosingStepState = offer => {
  const transaction = closingTransactionForOffer(offer);
  const status = normalizedStatus(transaction?.status);
  if (TRANSACTION_DONE_STATUSES.includes(status)) return 'done';
  if (TRANSACTION_CANCELED_STATUSES.includes(status)) return 'error';
  if (transaction) return 'current';
  return isContractSigned(contractForOffer(offer)) ? 'current' : 'pending';
};

const purchaseTicDescription = offer => {
  const contract = contractForOffer(offer);
  if (isContractSigned(contract) || isContractSent(contract)) {
    return 'TIC/CCV emitido e enviado para assinatura digital.';
  }
  return (
    purchaseContractBlockReason(offer) ||
    'Informe os dados necessários para a criação do documento'
  );
};

const purchaseSignatureDescription = offer => {
  if (!contractForOffer(offer)) return 'Anexe o TIC e o CCV assinado';
  return contractSignatureDescription(offer);
};

const purchaseClosingDescription = offer => {
  const transaction = closingTransactionForOffer(offer);
  if (!transaction) return purchaseClosingBlockReason(offer);

  const status = normalizedStatus(transaction.status);
  if (TRANSACTION_DONE_STATUSES.includes(status)) {
    return 'Transação concluída; venda finalizada.';
  }
  if (TRANSACTION_CANCELED_STATUSES.includes(status)) {
    return 'Transação de fechamento cancelada.';
  }
  return 'Acompanhe certidões, parcelas e itens obrigatórios no checklist da transação.';
};

export const purchaseOfferSteps = offer => {
  const accepted = offer?.status === 'accepted';
  const contract = contractForOffer(offer);
  const stepFor = (key, rest) => ({
    key,
    ...rest,
    actions: purchaseProgressActionsForStep(offer, key),
  });

  return [
    stepFor(PURCHASE_PROGRESS_STEP_KEYS.RECEIVED, {
      label: 'Proposta recebida',
      state: 'done',
    }),
    stepFor(PURCHASE_PROGRESS_STEP_KEYS.ACCEPTED, {
      label: accepted ? 'Proposta aceita' : 'Aceitar proposta',
      description: accepted
        ? offerAcceptedSummary(offer)
        : 'Informe os valores finais da transação',
      state: accepted ? 'done' : 'current',
    }),
    stepFor(PURCHASE_PROGRESS_STEP_KEYS.TIC, {
      label: 'Emissão do TIC (Termo de Intermediação Compartilhada)',
      description: purchaseTicDescription(offer),
      state: purchaseTicStepState(offer),
    }),
    stepFor(PURCHASE_PROGRESS_STEP_KEYS.SIGNATURE, {
      label: 'Assinatura de documentos',
      description: purchaseSignatureDescription(offer),
      state: contractSignatureStepState(
        isContractSigned(contract),
        isContractSent(contract)
      ),
    }),
    stepFor(PURCHASE_PROGRESS_STEP_KEYS.CLOSING, {
      label: 'Conclusão da venda',
      description: purchaseClosingDescription(offer),
      state: purchaseClosingStepState(offer),
    }),
  ];
};

export const offerProgressSteps = offer => {
  if (!offer) return [];
  const contract = contractForOffer(offer);
  const kyc = kycForOffer(offer);
  const kycApproved = isKycApproved(kyc);
  const contractSent = isContractSent(contract);
  const contractSigned = isContractSigned(contract);

  if (offer.status === 'declined' || offer.status === 'withdrawn') {
    return [
      { label: 'Proposta recebida', state: 'done' },
      { label: 'Proposta cancelada', state: 'current' },
    ];
  }

  if (!isRentalOffer(offer)) {
    return purchaseOfferSteps(offer);
  }

  if (isRentalOffer(offer) && offer.status === 'accepted') {
    return rentalAcceptedOfferSteps(offer);
  }

  return [
    {
      label: 'Proposta recebida',
      state: ['submitted', 'accepted'].includes(offer.status)
        ? 'done'
        : 'current',
    },
    {
      label:
        offer.status === 'accepted' ? 'Proposta aceita' : 'Aceitar proposta',
      description:
        offer.status === 'accepted'
          ? offerAcceptedSummary(offer)
          : 'Confira os valores antes de aceitar.',
      state: offer.status === 'accepted' ? 'done' : 'current',
    },
    {
      label: 'Documentação do proprietário',
      description:
        offer.status === 'accepted'
          ? 'Documentação pronta para o contrato'
          : 'Aguardando o envio dos dados e documentos do proprietário do imóvel',
      state: offer.status === 'accepted' ? 'done' : 'pending',
    },
    {
      label: 'Documentação do interessado',
      description: kycApproved
        ? 'KYC aprovado para liberar contrato'
        : 'Capture selfie/documento e envie para validação',
      state: kycStepState(offer, kycApproved),
    },
    {
      label: 'Preparação do contrato',
      description: contractSent
        ? 'CCV criado na ZapSign'
        : 'Gere o CCV quando a documentação estiver pronta',
      state: contractPreparationStepState(contractSent, kycApproved),
    },
    {
      label: 'Assinatura do contrato',
      description: contractSignatureDescription(offer),
      state: contractSignatureStepState(contractSigned, contractSent),
    },
  ];
};

const purchaseProgressAlert = offer => {
  const contract = contractForOffer(offer);

  if (isContractSigned(contract)) {
    const closingBlockReason = purchaseClosingBlockReason(offer);
    return {
      title: 'CCV assinado',
      description:
        closingBlockReason ||
        'Conclua o fechamento pelo checklist da transação: certidões, parcelas e itens obrigatórios.',
    };
  }

  if (isContractSent(contract)) {
    return {
      title: 'TIC/CCV enviado para assinatura',
      description: contractSignatureDescription(offer),
    };
  }

  const blockReason = purchaseContractBlockReason(offer);
  if (blockReason) {
    return {
      title: 'Emissão do TIC bloqueada',
      description: blockReason,
    };
  }

  return {
    title: 'Tudo pronto para emitir o TIC',
    description:
      'Documentação liberada: emita o TIC e o CCV por aqui e acompanhe as assinaturas nesta trilha.',
  };
};

export const offerProgressAlert = offer => {
  if (!offer || offer.status !== 'accepted') {
    return null;
  }

  if (isRentalOffer(offer)) {
    return {
      title: 'Proposta aceita',
      description:
        'Com tudo certo, em poucos dias o contrato será preparado. Acompanhe a documentação, a preparação e a assinatura por esta trilha.',
    };
  }

  return purchaseProgressAlert(offer);
};
